import { useAccounts } from './client-hierarchy';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BET_MODES, type BetMode } from '@lp/engine';
import type { BetLine, ClientProductView, ClientView, PlannedDraw, QuoteView, TicketListItem, TicketStatus, TicketView } from '@lp/contracts';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { errorText, fieldProblems, get, post } from '@/lib/api';
import { useScope } from '@/lib/scope';
import { dateTime, num } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { Badge, Button, Card, Dialog, ErrorBox, Field, Input, Loading, PageHeader, Select } from '@/components/ui';

export const money = (n: number) => `${num(n)} ៛`;

export function TicketStatusBadge({ status }: { status: TicketStatus }) {
  const t = useT();
  const tone = { open: 'blue', won: 'green', lost: 'gray', cancelled: 'amber' } as const;
  const label = { open: 'Open', won: 'Won', lost: 'Lost', cancelled: 'Cancelled' } as const;
  return <Badge tone={tone[status]}>{t(label[status])}</Badge>;
}

const MODE_LABEL: Record<BetMode, string> = {
  single: 'Single',
  multiply: 'Multiply (all arrangements)',
  roll_range: 'Roll from … to …',
  roll_odd: 'Roll odd',
  roll_even: 'Roll even',
  roll_head: 'Roll the head digit',
  roll_mid: 'Roll the middle digit',
  roll_tail: 'Roll the tail digit',
  roll_special: 'Roll special',
  roll_wild: 'Roll W',
  roll_wild_x: 'Roll W and multiply',
};

const blankLine = (): BetLine => ({ mode: 'single', input1: '', input2: '', digitLen: 2, postCodes: [], amountPerNumber: 1000 });

/** a client's tickets: the list, a test slip (quote, then sell), one ticket's numbers and winnings */
export function ClientTicketsPage() {
  const t = useT();
  const qc = useQueryClient();
  const scope = useScope();
  const superAdmin = scope.can('tickets', 'edit');
  const clientId = scope.clientId;
  const client = useQuery({ queryKey: ['client', clientId], queryFn: () => get<ClientView>(`/platform/clients/${clientId}`), enabled: !scope.company });
  const list = useQuery({ queryKey: ['client', clientId, 'tickets'], queryFn: () => get<TicketListItem[]>(`${scope.base}/tickets`) });
  const [open, setOpen] = useState<string | null>(null);
  const [selling, setSelling] = useState(false);

  return (
    <>
      {!scope.company && (
        <Link to={`/clients/${clientId}`} className="mb-2 inline-flex items-center gap-1 text-xs text-ink-500 hover:text-ink-800">
          <ArrowLeft size={12} /> {client.data?.nameEn ?? t('Client')}
        </Link>
      )}
      <PageHeader
        title={t('Tickets')}
        subtitle={t('Test sales prove a product end to end before a client goes live: the slip is priced by the betting engine and settled from the published result.')}
        actions={
          superAdmin && (
            <Button icon={<Plus size={14} />} onClick={() => setSelling(true)}>
              {t('Test ticket')}
            </Button>
          )
        }
      />
      {selling && (
        <SellSlip
          clientId={clientId}
          onDone={(ticket) => {
            setSelling(false);
            void qc.invalidateQueries({ queryKey: ['client', clientId, 'tickets'] });
            setOpen(ticket.ticketNo);
          }}
          onCancel={() => setSelling(false)}
        />
      )}
      <div className="rounded-lg border border-ink-200 bg-white">
        {list.isLoading ? (
          <div className="p-4">
            <Loading />
          </div>
        ) : list.error ? (
          <div className="p-4">
            <ErrorBox text={errorText(list.error)} />
          </div>
        ) : !list.data?.length ? (
          <p className="p-4 text-sm text-ink-600">{t('No ticket yet.')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ink-100 text-left text-xs text-ink-500">
                  <th className="px-4 py-2">{t('Ticket')}</th>
                  <th className="px-4 py-2">{t('Draw')}</th>
                  <th className="px-4 py-2 text-right">{t('Total')}</th>
                  <th className="px-4 py-2">{t('State')}</th>
                  <th className="px-4 py-2 text-right">{t('Won')}</th>
                  <th className="px-4 py-2">{t('Sold')}</th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((x) => (
                  <tr key={x.ticketNo} onClick={() => setOpen(x.ticketNo)} className="cursor-pointer border-b border-ink-100 last:border-0 hover:bg-ink-50" data-testid={`ticket-${x.ticketNo}`}>
                    <td className="px-4 py-2 font-mono text-xs">{x.ticketNo}</td>
                    <td className="px-4 py-2 text-xs">
                      <span className="font-mono">
                        {x.productCode} {x.gameCode !== x.productCode ? x.gameCode : ''}
                      </span>{' '}
                      {x.date} {x.time}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">{money(x.total)}</td>
                    <td className="px-4 py-2">
                      <TicketStatusBadge status={x.status} />
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">{x.winAmount ? money(x.winAmount) : '—'}</td>
                    <td className="px-4 py-2 text-xs text-ink-500">{dateTime(x.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {open && <TicketDialog clientId={clientId} ticketNo={open} onClose={() => setOpen(null)} />}
    </>
  );
}

function SellSlip({ clientId, onDone, onCancel }: { clientId: number; onDone: (t: TicketView) => void; onCancel: () => void }) {
  const t = useT();
  const scope = useScope();
  const mine = useQuery({ queryKey: ['client', clientId, 'products'], queryFn: () => get<ClientProductView[]>(`${scope.base}/products`) });
  const active = (mine.data ?? []).filter((p) => p.status === 'active');
  const [product, setProduct] = useState('');
  const code = product || active[0]?.productCode || '';
  const schedule = useQuery({ queryKey: ['product', code, 'schedule', 3], queryFn: () => get<{ draws: PlannedDraw[] }>(scope.schedule(code)), enabled: !!code });
  // draws still open: at least one post not past its close
  const draws = useMemo(() => (schedule.data?.draws ?? []).filter((d) => d.posts.some((p) => new Date(p.closeAt).getTime() > Date.now())), [schedule.data]);
  const [drawKey, setDrawKey] = useState('');
  const draw = draws.find((d) => `${d.gameCode} ${d.date} ${d.time}` === drawKey) ?? draws[0];
  const [lines, setLines] = useState<BetLine[]>([blankLine()]);
  const [idem] = useState(() => crypto.randomUUID());
  const sellers = (useAccounts(clientId, scope.can('accounts', 'read')).data ?? []).filter((a) => a.canSell && a.status === 'active');
  const [seller, setSeller] = useState('');
  const body = () => ({ product: code, game: draw!.gameCode, date: draw!.date, time: draw!.time, lines: lines.map((l) => ({ ...l, input2: l.input2 || null })) });
  const quote = useMutation({ mutationFn: () => post<QuoteView>(`${scope.base}/quote`, body()) });
  const sell = useMutation({ mutationFn: () => post<TicketView>(`${scope.base}/tickets`, { ...body(), idempotencyKey: idem, ...(seller ? { accountId: Number(seller) } : {}) }), onSuccess: onDone });
  const setLine = (i: number, change: Partial<BetLine>) => {
    setLines((cur) => cur.map((l, j) => (j === i ? { ...l, ...change } : l)));
    quote.reset();
  };
  const error = quote.error ?? sell.error;

  return (
    <Card title={t('Test ticket')} className="mb-4" actions={<Button variant="ghost" onClick={onCancel}>{t('Cancel')}</Button>}>
      {mine.isLoading ? (
        <Loading />
      ) : !active.length ? (
        <p className="text-sm text-ink-600">{t('Install a product for this client first.')}</p>
      ) : (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('Product')}>
              <Select
                value={code}
                onChange={(e) => {
                  setProduct(e.target.value);
                  setDrawKey('');
                  quote.reset();
                }}
                className="w-full"
              >
                {active.map((p) => (
                  <option key={p.productCode} value={p.productCode}>
                    {p.productCode} — {p.nameEn} (v{p.version})
                  </option>
                ))}
              </Select>
            </Field>
            {sellers.length > 0 && (
              <Field label={t('Sold by')}>
                <Select value={seller} onChange={(e) => setSeller(e.target.value)} className="w-full" aria-label={t('Sold by')}>
                  <option value="">{t('No account (platform test)')}</option>
                  {sellers.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.path.join(' / ')} — {a.name}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
            <Field label={t('Draw')}>
              <Select value={draw ? `${draw.gameCode} ${draw.date} ${draw.time}` : ''} onChange={(e) => setDrawKey(e.target.value)} className="w-full">
                {draws.map((d) => (
                  <option key={`${d.gameCode} ${d.date} ${d.time}`} value={`${d.gameCode} ${d.date} ${d.time}`}>
                    {d.datePP} {d.timePP} · {d.gameCode}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          {!draw ? (
            <p className="text-sm text-ink-600">{t('No open draw in the next days.')}</p>
          ) : (
            <>
              <div className="space-y-2">
                {lines.map((l, i) => (
                  <div key={i} className="flex flex-wrap items-end gap-2 rounded-md border border-ink-200 p-2" data-testid={`slip-line-${i}`}>
                    <Field label={t('Number')}>
                      <Input
                        value={l.input1}
                        onChange={(e) => {
                          const v = e.target.value.toUpperCase();
                          setLine(i, { input1: v, digitLen: v.replace(/[^0-9W]/g, '').length === 3 ? 3 : 2 });
                        }}
                        className="w-24 font-mono"
                        aria-label={`${t('Number')} ${i + 1}`}
                      />
                    </Field>
                    <Field label={t('How')}>
                      <Select value={l.mode} onChange={(e) => setLine(i, { mode: e.target.value as BetMode })}>
                        {BET_MODES.map((m) => (
                          <option key={m} value={m}>
                            {t(MODE_LABEL[m])}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    {l.mode === 'roll_range' && (
                      <Field label={t('To')}>
                        <Input value={l.input2 ?? ''} onChange={(e) => setLine(i, { input2: e.target.value })} className="w-20 font-mono" />
                      </Field>
                    )}
                    <Field label={t('Digits')}>
                      <Select value={l.digitLen} onChange={(e) => setLine(i, { digitLen: Number(e.target.value) as 2 | 3 })}>
                        <option value={2}>2D</option>
                        <option value={3}>3D</option>
                      </Select>
                    </Field>
                    <div>
                      <span className="mb-1 block text-xs font-medium text-ink-600">{t('Posts')}</span>
                      <div className="flex flex-wrap gap-1">
                        {draw.posts.map((p) => {
                          const on = l.postCodes.includes(p.code);
                          const closed = new Date(p.closeAt).getTime() <= Date.now();
                          return (
                            <button
                              key={p.code}
                              type="button"
                              disabled={closed}
                              onClick={() => setLine(i, { postCodes: on ? l.postCodes.filter((x) => x !== p.code) : [...l.postCodes, p.code] })}
                              className={`h-8 min-w-8 rounded border px-1.5 font-mono text-xs ${on ? 'border-brand-600 bg-brand-600 text-white' : 'border-ink-200 bg-white text-ink-800'} disabled:opacity-30`}
                              aria-pressed={on}
                              title={`${t('closes')} ${p.closePP.slice(11)}`}
                            >
                              {p.code}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <Field label={t('Amount per number')}>
                      <Input type="number" min={0} step={50} value={l.amountPerNumber} onChange={(e) => setLine(i, { amountPerNumber: Number(e.target.value) })} className="w-28" />
                    </Field>
                    <Button variant="ghost" onClick={() => setLines((cur) => cur.filter((_, j) => j !== i))} disabled={lines.length === 1} aria-label={t('Remove')}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                ))}
                <Button variant="secondary" icon={<Plus size={13} />} onClick={() => setLines((cur) => [...cur, blankLine()])}>
                  {t('Add a line')}
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" onClick={() => quote.mutate()} disabled={quote.isPending}>
                  {t('Price it')}
                </Button>
                <Button onClick={() => sell.mutate()} disabled={!quote.data || sell.isPending}>
                  {t('Sell')} {quote.data ? money(quote.data.total) : ''}
                </Button>
              </div>
              {error && <ErrorBox text={[errorText(error), ...fieldProblems(error)].join(' — ')} />}
              {quote.data && (
                <div className="rounded-md bg-ink-50 p-3 text-sm" data-testid="quote">
                  {quote.data.lines.map((l, i) => (
                    <div key={i} className="flex flex-wrap justify-between gap-2 border-b border-ink-100 py-1 last:border-0">
                      <span>
                        <span className="font-mono">{l.label}</span> · {l.digitLen}D · <span className="font-mono">{l.postCodes.join(' ')}</span> · {l.numberCount} × {money(l.amountPerNumber)}
                        {l.multiplierSum !== l.postCodes.length && <span className="text-ink-500"> · ×{l.multiplierSum}</span>}
                      </span>
                      <span className="font-medium tabular-nums">{money(l.lineTotal)}</span>
                    </div>
                  ))}
                  <div className="mt-2 flex justify-between font-semibold">
                    <span>
                      {t('Total')} · {t('closes')} {quote.data.closesAtPP.slice(11)}
                    </span>
                    <span className="tabular-nums">{money(quote.data.total)}</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </Card>
  );
}

function TicketDialog({ clientId, ticketNo, onClose }: { clientId: number; ticketNo: string; onClose: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const scope = useScope();
  const superAdmin = scope.can('tickets', 'edit');
  const ticket = useQuery({ queryKey: ['ticket', clientId, ticketNo], queryFn: () => get<TicketView>(`${scope.base}/tickets/${ticketNo}`) });
  const [reason, setReason] = useState('');
  const cancel = useMutation({
    mutationFn: () => post<TicketView>(`${scope.base}/tickets/${ticketNo}/cancel`, { reason }),
    onSuccess: (v) => {
      qc.setQueryData(['ticket', clientId, ticketNo], v);
      void qc.invalidateQueries({ queryKey: ['client', clientId, 'tickets'] });
    },
  });
  const x = ticket.data;
  return (
    <Dialog title={`${t('Ticket')} ${ticketNo}`} onClose={onClose} footer={<Button variant="secondary" onClick={onClose}>{t('Close')}</Button>}>
      {!x ? (
        <Loading />
      ) : (
        <div className="space-y-3" data-testid="ticket-dialog">
          <div className="flex flex-wrap items-center gap-2">
            <TicketStatusBadge status={x.status} />
            <span className="font-mono text-xs">
              {x.productCode} {x.gameCode} · {x.datePP} {x.timePP} · v{x.version}
            </span>
          </div>
          {x.lines.map((l) => (
            <div key={l.idx} className="rounded border border-ink-200 p-2 text-xs">
              <div className="flex justify-between">
                <span>
                  <span className="font-mono font-semibold">{l.label}</span> · {l.digitLen}D · <span className="font-mono">{l.postCodes.join(' ')}</span>
                </span>
                <span className="tabular-nums">{money(l.lineTotal)}</span>
              </div>
              <div className="mt-1 break-all font-mono text-ink-600">
                {l.numbers.join(' ')}
                {l.numberCount > l.numbers.length ? ' …' : ''}
              </div>
              {l.hits.length > 0 && (
                <div className="mt-1 text-emerald-700">
                  {t('Won')}: {l.hits.map((h) => `${h.post} ${h.number}${h.times > 1 ? ` ×${h.times}` : ''}`).join(', ')} → {money(l.winAmount)}
                </div>
              )}
            </div>
          ))}
          <div className="flex justify-between border-t border-ink-100 pt-2 text-sm font-semibold">
            <span>{t('Total')}</span>
            <span>{money(x.total)}</span>
          </div>
          {x.status !== 'open' && x.status !== 'cancelled' && (
            <div className="flex justify-between text-sm font-semibold text-emerald-700">
              <span>{t('Won')}</span>
              <span>{money(x.winAmount)}</span>
            </div>
          )}
          {x.status === 'cancelled' && <p className="text-xs text-amber-800">{x.cancelReason}</p>}
          {x.account && (
            <p className="text-xs text-ink-500">
              {t('Sold by')}: {x.account.name} <span className="font-mono">{x.account.code}</span>
            </p>
          )}
          {x.payout && (
            <p className="text-xs text-ink-500">
              {t('Sold with the client’s own payout')}: 2D ×{x.payout['2']} · 3D ×{x.payout['3']}
            </p>
          )}
          {x.footer && <p className="border-t border-dashed border-ink-200 pt-2 text-center text-xs text-ink-600">{x.footer}</p>}
          {superAdmin && x.status === 'open' && (
            <div className="flex flex-wrap items-end gap-2 border-t border-ink-100 pt-2">
              <Field label={t('Reason to cancel')}>
                <Input value={reason} onChange={(e) => setReason(e.target.value)} className="w-56" maxLength={200} />
              </Field>
              <Button variant="secondary" onClick={() => cancel.mutate()} disabled={reason.trim().length < 3 || cancel.isPending}>
                {t('Cancel ticket')}
              </Button>
            </div>
          )}
          {cancel.error && <ErrorBox text={errorText(cancel.error)} />}
        </div>
      )}
    </Dialog>
  );
}
